import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useLanguage } from '@/contexts/LanguageContext';
import { loc } from '@/lib/localize';

export interface ExperienceDetail {
  id: string;
  title: string;
  slug: string;
  price_from: number;
  category: string;
  group_type: string;
  region: string;
  image: string;
  created_at: string;
}

interface ExperienceRow extends ExperienceDetail {
  title_vi: string | null;
  category_vi: string | null;
  group_type_vi: string | null;
  region_vi: string | null;
}

export function useExperienceDetail(experienceId: string) {
  const [row, setRow] = useState<ExperienceRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { language } = useLanguage();

  useEffect(() => {
    let cancelled = false;

    async function fetchExperience() {
      try {
        setLoading(true);
        setError(null);

        const { data, error: err } = await supabase
          .from('vietnamtravel_experiences')
          .select('*')
          .eq('id', experienceId)
          .maybeSingle();

        if (err) throw err;
        if (!cancelled) setRow(data as ExperienceRow | null);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load experience');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (experienceId) fetchExperience();
    return () => { cancelled = true; };
  }, [experienceId]);

  const experience: ExperienceDetail | null = row
    ? {
        id: row.id,
        title: loc(language, row.title, row.title_vi),
        slug: row.slug,
        price_from: row.price_from,
        category: loc(language, row.category, row.category_vi),
        group_type: loc(language, row.group_type, row.group_type_vi),
        region: row.region,
        image: row.image,
        created_at: row.created_at,
      }
    : null;

  return { experience, loading, error };
}